import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ReferralOptInDialogComponent } from '@app/+authenticated/referral-opt-in-dialog/referral-opt-in-dialog.component';
import { selectAuthCelloJwt } from '@app/reducers/auth/auth.selectors';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { SbDialogService } from '@sb/ui/dialog';
import { ToastService } from '@sb/ui/toast';
import { map, take } from 'rxjs';

import { CELLO_QUERY_PARAM_VALUES, CelloService } from './cello.service';
import { TrackingService } from './tracking.service';
import { UserPreferencesService } from './user-preferences.service';

@Injectable({ providedIn: 'root' })
export class ReferralProgramService {
  private store = inject<Store<AppState>>(Store);
  private route = inject(ActivatedRoute);
  private featureService = inject(FeatureService);
  private celloService = inject(CelloService);
  private trackingService = inject(TrackingService);
  private userPreferencesService = inject(UserPreferencesService);
  private dialog = inject(SbDialogService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  private celloJwt = this.store.selectSignal(selectAuthCelloJwt);
  private referralProgramOptedIn = signal<boolean | null>(null);

  private shouldOpenReferralWidget = toSignal(
    this.route.queryParamMap.pipe(map((params) => !!CELLO_QUERY_PARAM_VALUES.includes(params.get('cello-open')))),
  );

  isVisible = computed(() => this.featureService.isEnabled(featureFlag.CELLO) && !!this.celloJwt());

  constructor() {
    this.userPreferencesService
      .getReferralConsent()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((hasOptedIn) => {
        this.referralProgramOptedIn.set(hasOptedIn);
        if (this.isVisible() && hasOptedIn) {
          this.celloService.initialize().catch((error) => {
            console.error('Error initializing Cello:', error);
          });
        }
      });

    effect(() => {
      if (this.isVisible() && this.shouldOpenReferralWidget() && this.referralProgramOptedIn() !== null) {
        if (this.referralProgramOptedIn()) {
          this.openCelloWidget();
          return;
        }

        this.openOptInDialog();
      }
    });
  }

  handleClick(originator: string): void {
    void this.trackingService.event('Referral Element Clicked', { originator });

    if (this.referralProgramOptedIn()) {
      this.openCelloWidget();
      return;
    }

    this.openOptInDialog();
  }

  private openOptInDialog(): void {
    this.dialog
      .setSize('sm')
      .open(ReferralOptInDialogComponent)
      .closed.pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((hasOptedIn: boolean) => {
        if (hasOptedIn) {
          this.trackingService.event('Referral Program Opted In');
          this.referralProgramOptedIn.set(hasOptedIn);
          this.openCelloWidget();
          return;
        }

        this.trackingService.event('Referral Program Opt In Dismissed');
      });
  }

  private openCelloWidget(): void {
    this.celloService.openWidget().catch(() => {
      this.toast.openDanger(this.translate.instant('Something went wrong trying to open the referral widget'));
    });
  }
}
